import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import useAuthentication from "../../hooks/useAuthentication";
import Gravatar from "../../libs/Gravatar";
import ActivityFeed from "../Activity/ActivityFeed";

const UserProfile = () => {
    const Auth = useAuthentication();
    const CurrentUser = useSelector((state) => state.CurrentUser);
    const UserSettings = useSelector((state) => state.User);
    const Team = useSelector((state) => state.Team);
    const location = useLocation();

    // Kennwort ändern
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordMessage, setPasswordMessage] = useState("");
    const [passwordMessageType, setPasswordMessageType] = useState(""); // success oder danger

    const [activeTab, setActiveTab] = useState("password");

    // Check URL hash to set initial tab
    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash && ['password', 'activity', 'settings'].includes(hash)) {
            setActiveTab(hash);
        }
    }, [location]);

    const showLegacyPasswordWarning = () => {
        // Create overlay
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(45deg, #ff0000, #ff7700, #ffdd00, #00ff00, #0099ff, #6600ff, #ff0099);
            background-size: 400% 400%;
            animation: rainbowGradient 3s ease infinite;
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.3s ease;
        `;

        // Create warning box
        const warningBox = document.createElement('div');
        warningBox.style.cssText = `
            background: white;
            padding: 40px;
            border-radius: 20px;
            box-shadow: 0 0 50px rgba(0,0,0,0.5);
            text-align: center;
            max-width: 600px;
            animation: shake 0.5s infinite, pulse 1s infinite;
            transform: scale(0);
            transition: transform 0.3s ease;
        `;

        warningBox.innerHTML = `
            <div style="font-size: 80px; margin-bottom: 20px; animation: spin 2s linear infinite;">
                🚨
            </div>
            <h1 style="color: #dc3545; font-size: 2.5em; margin-bottom: 20px; text-shadow: 2px 2px 4px rgba(0,0,0,0.3); animation: colorChange 2s infinite;">
                ACHTUNG! ALTES STANDARDKENNWORT!
            </h1>
            <p style="font-size: 1.3em; margin-bottom: 30px; color: #333;">
                Dieses Kennwort gehört zu unseren alten Standardkennwörtern<br>
                und sollte <strong>NICHT MEHR</strong> verwendet werden! 😱
            </p>
            <p style="font-size: 1.5em; margin-bottom: 30px;">
                🎉 Bitte wähle ein <strong>neues, sicheres</strong> Kennwort! 🎉
            </p>
            <button id="legacyPasswordOk" style="
                background: linear-gradient(45deg, #dc3545, #ff6b6b);
                color: white;
                border: none;
                padding: 15px 40px;
                font-size: 1.2em;
                border-radius: 50px;
                cursor: pointer;
                box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
                animation: buttonPulse 1s infinite;
                font-weight: bold;
            ">
                OK, ich wähle ein neues Kennwort! ✨
            </button>
        `;

        overlay.appendChild(warningBox);
        document.body.appendChild(overlay);

        // Add CSS animations
        const style = document.createElement('style');
        style.textContent = `
            @keyframes rainbowGradient {
                0% { background-position: 0% 50%; }
                50% { background-position: 100% 50%; }
                100% { background-position: 0% 50%; }
            }
            @keyframes shake {
                0%, 100% { transform: translateX(0) scale(1); }
                25% { transform: translateX(-10px) scale(1); }
                75% { transform: translateX(10px) scale(1); }
            }
            @keyframes pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.05); }
            }
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
            @keyframes colorChange {
                0%, 100% { color: #dc3545; }
                25% { color: #ff6b6b; }
                50% { color: #ff0000; }
                75% { color: #ff4444; }
            }
            @keyframes buttonPulse {
                0%, 100% { transform: scale(1); box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4); }
                50% { transform: scale(1.1); box-shadow: 0 6px 25px rgba(220, 53, 69, 0.6); }
            }
        `;
        document.head.appendChild(style);

        // Animate in
        setTimeout(() => {
            overlay.style.opacity = '1';
            warningBox.style.transform = 'scale(1)';
        }, 10);

        // Handle close
        document.getElementById('legacyPasswordOk').addEventListener('click', () => {
            overlay.style.opacity = '0';
            warningBox.style.transform = 'scale(0)';
            setTimeout(() => {
                document.body.removeChild(overlay);
                document.head.removeChild(style);
            }, 300);
            setNewPassword("");
            setConfirmPassword("");
        });
    };

    const changePassword = () => {
        setPasswordMessage("");

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordMessage("Bitte alle Felder ausfüllen");
            setPasswordMessageType("danger");
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMessage("Neue Kennwörter stimmen nicht überein");
            setPasswordMessageType("danger");
            return;
        }

        if (newPassword.length < 6) {
            setPasswordMessage("Das neue Kennwort muss mindestens 6 Zeichen lang sein");
            setPasswordMessageType("danger");
            return;
        }

        // Check for old standard passwords
        const lowerPassword = newPassword.toLowerCase();
        if (lowerPassword.includes("kenevivo") || lowerPassword.includes("homer")) {
            showLegacyPasswordWarning();
            return;
        }

        axios({
            method: "post",
            url: SyncConstants["syncBaseURL"] + "user/changePassword",
            auth: Auth.getAuthObject(),
            data: {
                currentPassword: currentPassword,
                newPassword: newPassword
            }
        }).then(response => {
            if (response.data.success) {
                setPasswordMessage("Kennwort erfolgreich geändert!");
                setPasswordMessageType("success");
                setCurrentPassword("");
                setNewPassword("");
                setConfirmPassword("");
            } else {
                setPasswordMessage(response.data.error || "Fehler beim Ändern des Kennworts");
                setPasswordMessageType("danger");
            }
        }).catch(error => {
            console.error("Error changing password:", error);
            setPasswordMessage("Fehler beim Ändern des Kennworts");
            setPasswordMessageType("danger");
        });
    };

    const getRealNameFromUsername = (username) => {
        if (!username) return '-';
        if (Team && Team[username]) {
            return Team[username].realName || username;
        }
        return username;
    };

    return (
        <div className="container mt-5">
            <div className="row">
                <div className="col-lg-8 offset-lg-2">
                    {/* Header */}
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-body">
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <Gravatar
                                        email={UserSettings.email}
                                        size={80}
                                        className="rounded-circle"
                                    />
                                </div>
                                <div className="ms-4">
                                    <h3 className="mb-1">{getRealNameFromUsername(CurrentUser.data?.userName)}</h3>
                                    <p className="text-muted mb-0">
                                        <i className="bi bi-envelope me-1"></i>
                                        {UserSettings.email}
                                    </p>
                                    {CurrentUser.data?.role && (
                                        <span className={`badge ${CurrentUser.data.role === 'administrator' ? 'bg-danger' : 'bg-secondary'} mt-2`}>
                                            {CurrentUser.data.role === 'administrator' ? 'Administrator' : 'Benutzer'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabs */}
                    <ul className="nav nav-tabs mb-4">
                        <li className="nav-item">
                            <a
                                className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('password');
                                }}
                            >
                                <i className="bi bi-key me-2"></i>
                                Kennwort ändern
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${activeTab === 'activity' ? 'active' : ''}`}
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('activity');
                                }}
                            >
                                <i className="bi bi-activity me-2"></i>
                                Aktivitäten
                            </a>
                        </li>
                        <li className="nav-item">
                            <a
                                className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveTab('settings');
                                }}
                            >
                                <i className="bi bi-gear me-2"></i>
                                Einstellungen
                            </a>
                        </li>
                    </ul>

                    {/* Kennwort ändern */}
                    {activeTab === 'password' && (
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">
                                    <i className="bi bi-shield-lock me-2"></i>
                                    Kennwort ändern
                                </h5>
                            </div>
                            <div className="card-body">
                                {passwordMessage && (
                                    <div className={`alert alert-${passwordMessageType}`}>
                                        {passwordMessage}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Aktuelles Kennwort</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                        placeholder="Aktuelles Kennwort eingeben"
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Neues Kennwort</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        placeholder="Neues Kennwort eingeben"
                                    />
                                    <small className="text-muted">Mindestens 6 Zeichen</small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Neues Kennwort bestätigen</label>
                                    <input
                                        type="password"
                                        className="form-control"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Neues Kennwort wiederholen"
                                    />
                                </div>

                                <button
                                    className="btn btn-primary"
                                    onClick={changePassword}
                                >
                                    <i className="bi bi-check-lg me-2"></i>
                                    Kennwort ändern
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Aktivitäten */}
                    {activeTab === 'activity' && (
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">
                                    <i className="bi bi-clock-history me-2"></i>
                                    Ihre letzten Aktivitäten
                                </h5>
                            </div>
                            <div className="card-body">
                                <ActivityFeed limit={50} compact={false} />
                            </div>
                        </div>
                    )}

                    {/* Einstellungen */}
                    {activeTab === 'settings' && (
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-light">
                                <h5 className="mb-0">
                                    <i className="bi bi-sliders me-2"></i>
                                    Persönliche Einstellungen
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="alert alert-info">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Weitere Einstellungen werden in Zukunft hier verfügbar sein.
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Sprache</label>
                                    <select className="form-select" disabled>
                                        <option>Deutsch</option>
                                    </select>
                                    <small className="text-muted">Weitere Sprachen folgen in Zukunft</small>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Zeitzone</label>
                                    <select className="form-select" disabled>
                                        <option>Europa/Berlin (MEZ)</option>
                                    </select>
                                    <small className="text-muted">Zeitzonenauswahl folgt in Zukunft</small>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserProfile;
